import { getLogger } from '@logtape/logtape';
import { useRef } from 'react';
import useSWR from 'swr';

import {
    APP_VERSION_CHECK_DEDUPING_INTERVAL_MS,
    APP_VERSION_CHECK_FOCUS_THROTTLE_MS,
    APP_VERSION_CHECK_INTERVAL_MS,
} from '@/constants/app-update.ts';

import type { VersionManifest } from '@/schemas/version-manifest.schema.ts';

import { fetchVersionManifest } from '@/services/app-version-service.ts';
import { showAppUpdateNotification } from '@/services/notificationService.ts';

import { getAppVersion } from '@/utils/envUtils.ts';

const logger = getLogger(['hook', 'app-version-check']);

/**
 * Polls the build-time version manifest and prompts the user to reload when a newer version has been
 * deployed. Detection compares the deployed `version.json` against the version baked into the running
 * bundle (`getAppVersion`). Fire-and-forget: mounted once at the app shell, no return value.
 */
export const useAppVersionCheck = (): void => {
    // The deployed version we have already prompted for. Lets each distinct new deploy prompt once, even
    // after the user dismisses an earlier toast, without re-prompting for the same version on every poll.
    const lastPromptedVersionRef = useRef<string | undefined>(undefined);

    // Uses useSWR (not the repo-default useSWRImmutable) on purpose: the check must re-run when the user
    // refocuses the tab or the network reconnects, which is the documented exception to the immutable default.
    useSWR<VersionManifest, Error>({ key: 'app-version-check' }, fetchVersionManifest, {
        refreshInterval: APP_VERSION_CHECK_INTERVAL_MS,
        focusThrottleInterval: APP_VERSION_CHECK_FOCUS_THROTTLE_MS,
        dedupingInterval: APP_VERSION_CHECK_DEDUPING_INTERVAL_MS,
        onSuccess: (manifest: VersionManifest): void => {
            if (manifest.version === getAppVersion()) return;
            if (manifest.version === lastPromptedVersionRef.current) return;

            lastPromptedVersionRef.current = manifest.version;
            showAppUpdateNotification();
        },
        onError: (error: Error): void => {
            // A failed background version check should not interrupt the user; log and retry on the next poll.
            logger.warn('App version check failed', { error: error });
        },
    });
};
